'use client';

import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogPanel } from '@headlessui/react';
import { Menu as MenuIcon, X } from 'lucide-react';
import { useMobileMenuNavigation } from '@/hooks/use-mobile-menu-navigation';
import type { Locale, StaticTranslationString } from '@/types/types';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
const openMenuText: StaticTranslationString = {
  de: 'Hauptmenü öffnen',
  en: 'Open main menu',
  fr: 'Ouvrir le menu principal'
};
const closeMenuText: StaticTranslationString = {
  de: 'Menü schliessen',
  en: 'Close menu',
  fr: 'Fermer le menu'
};
export const NavComponent: React.FC<{
  children?: React.ReactNode;
  showDesktopSidePanel?: boolean;
  // A prop, not `useCurrentLocale`: the header is part of the prerendered shell, which must not
  // read the pathname.
  locale: Locale;
}> = t0 => {
  const $ = _c(37);
  const {
    children,
    showDesktopSidePanel: t1,
    locale
  } = t0;
  const showDesktopSidePanel = t1 === undefined ? true : t1;
  const {
    mobileMenuOpen,
    setMobileMenuOpen,
    checkClickEvent
  } = useMobileMenuNavigation();
  const justClosedReference = useRef(false);
  let t2;
  if ($[0] !== setMobileMenuOpen) {
    t2 = () => {
      setMobileMenuOpen(false);
      justClosedReference.current = true;
      setTimeout(() => {
        justClosedReference.current = false;
      }, 200);
    };
    $[0] = setMobileMenuOpen;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClose = t2;
  let t3;
  if ($[2] !== setMobileMenuOpen) {
    t3 = () => {
      if (justClosedReference.current) {
        return;
      }
      setMobileMenuOpen(true);
    };
    $[2] = setMobileMenuOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleOpen = t3;
  let t4;
  if ($[4] !== handleClose) {
    t4 = () => {
      if (window?.innerWidth >= 1280) {
        handleClose();
      }
    };
    $[4] = handleClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleResize = t4;
  let t5;
  let t6;
  if ($[6] !== handleResize) {
    t5 = () => {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t6 = [handleResize];
    $[6] = handleResize;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== handleOpen || $[10] !== locale || $[11] !== mobileMenuOpen) {
    t7 = !mobileMenuOpen && <button type="button" onClick={handleOpen} className="relative top-[18px] z-[105] cursor-pointer"><span className="sr-only">{openMenuText[locale]}</span><MenuIcon aria-hidden="true" className="size-6" /></button>;
    $[9] = handleOpen;
    $[10] = locale;
    $[11] = mobileMenuOpen;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handleClose || $[14] !== locale || $[15] !== mobileMenuOpen) {
    t8 = mobileMenuOpen && <button type="button" className="relative top-[18px] z-[105] cursor-pointer" onClick={handleClose}><span className="sr-only">{closeMenuText[locale]}</span><X aria-hidden="true" className="size-6" /></button>;
    $[13] = handleClose;
    $[14] = locale;
    $[15] = mobileMenuOpen;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="fixed inset-0 z-[90] cursor-pointer bg-black/20" />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== checkClickEvent) {
    t10 = event => checkClickEvent(event);
    $[18] = checkClickEvent;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== children || $[21] !== t10) {
    t11 = <DialogPanel className="fixed inset-y-0 right-0 left-0 z-[95] mt-[calc(62px+var(--preview-banner-height))] w-full overflow-y-scroll bg-white px-4 pt-4 pb-6 shadow-2xl"><div onClick={t10}>{children}</div></DialogPanel>;
    $[20] = children;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== handleClose || $[24] !== mobileMenuOpen || $[25] !== t11) {
    t12 = <Dialog open={mobileMenuOpen} onClose={handleClose}>{t9}{t11}</Dialog>;
    $[23] = handleClose;
    $[24] = mobileMenuOpen;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t7 || $[29] !== t8) {
    t13 = <div className="h-[60px] xl:hidden">{t7}{t8}{t12}</div>;
    $[27] = t12;
    $[28] = t7;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== children || $[32] !== showDesktopSidePanel) {
    t14 = showDesktopSidePanel && <div className="fixed top-[calc(62px+var(--preview-banner-height))] left-0 hidden h-full w-96 border-r-2 border-gray-200 bg-white py-8 xl:block">{children}</div>;
    $[31] = children;
    $[32] = showDesktopSidePanel;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13 || $[35] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
};